import React, { useEffect, useState } from "react";
import { useUser } from "../../context/UserContext";
import { getCustomerSales } from "../../services/sales";
import axiosInstance from "../../services/api/axios";
import { FormModal, Form, Field, FormSubmit } from "../../components/form";
import { cancellationSchema } from "../../services/sales/schemas";

/**
 * Pick the right sale row for cancellation. Priority: null cancellationStatus
 * (an active sale) → "Initiated" (resume a started cancellation) → anything
 * else. Returns null when there's nothing to cancel.
 */
const filterSaleData = (sales) => {
  if (!Array.isArray(sales) || sales.length === 0) return null;
  const active = sales.find((s) => s.cancellationStatus === null);
  if (active) return active;
  const initiated = sales.find((s) => s.cancellationStatus === "Initiated");
  if (initiated) return initiated;
  return sales[0] || null;
};

const CancellationModal = ({ isOpen, onClose, propertyId, customerSaleId, customerId }) => {
  const [saleData, setSaleData] = useState(null);
  const [cancellationProof, setCancellationProof] = useState(null);
  const { userInfo: _userInfo } = useUser(); // eslint-disable-line no-unused-vars

  // Load the sale data so we can show the base price and pre-fill defaults.
  useEffect(() => {
    if (!isOpen || !customerSaleId) return;
    let cancelled = false;
    (async () => {
      try {
        const salesResponse = await getCustomerSales({ customerId, customerSaleId });
        const filtered = filterSaleData(salesResponse);
        if (!cancelled) setSaleData(filtered);
      } catch (error) {
        console.error("Error fetching sale data:", error);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, customerSaleId, propertyId, customerId]);

  const handleSubmit = async (values) => {
    if (!saleData) return;
    try {
      const fd = new FormData();
      fd.append("customer_sales_id", customerSaleId);
      fd.append(
        "cancellation_initiated_date",
        new Date().toISOString().split("T")[0],
      );
      if (cancellationProof) fd.append("medium", cancellationProof);
      fd.append("cancellation_reason", values.cancellationReason);
      fd.append("refund_amount", values.refundAmount ?? 0);
      fd.append("refund_amount_details", "Refund amount details");

      await axiosInstance.post(`/customer/sales/cancellation`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Refetch sale data to hand the updated property back to the parent.
      const refreshed = await getCustomerSales({ customerId, customerSaleId });
      const filtered = filterSaleData(refreshed);
      onClose?.({
        activeTab: "cancellation",
        property: filtered?.propertyData ?? null,
      });
    } catch (error) {
      console.error("Error creating cancellation:", error);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={() => onClose?.()}
      title="Cancel Sale"
      size="md"
    >
      <Form
        schema={cancellationSchema}
        defaultValues={{
          cancellationReason: "",
          refundAmount: "",
        }}
        onSubmit={handleSubmit}
      >
        {/* File upload stays outside RHF — parent owns the file ref */}
        <div>
          <label className="block text-sm font-normal text-slate-500 mb-1">
            Upload Cancellation Proof
          </label>
          <input
            type="file"
            onChange={(e) => setCancellationProof(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-flamingo-50 file:text-flamingo-700 hover:file:bg-flamingo-100"
          />
          {cancellationProof && (
            <p className="mt-1 text-xs text-slate-500 truncate">
              ✓ {cancellationProof.name}
            </p>
          )}
        </div>

        <Field
          name="cancellationReason"
          label="Cancellation Reason"
          type="textarea"
          placeholder="Enter cancellation reason"
          rows={3}
          required
        />

        <div className="p-4 border rounded">
          <h6 className="mb-2 font-medium text-md text-gray-700">Refund Details</h6>
          <div className="flex items-center mb-3">
            <p className="text-gray-700 mr-2 text-sm">Base Price:</p>
            <p className="text-black text-sm">₹ {saleData?.basePrice ?? 0}</p>
          </div>
          <Field
            name="refundAmount"
            label="Total Refund Amount"
            type="number"
            min={0}
            step="0.01"
            placeholder="Enter Amount"
          />
        </div>

        <FormSubmit loadingLabel="Saving...">Save</FormSubmit>
      </Form>
    </FormModal>
  );
};

export default CancellationModal;
